import {
    Button,
    STYLE_FILL,
    STYLE_REBRANDING_NEUTRAL,
} from '@eventbrite/eds-button';
import { gettext } from '@eventbrite/i18n';
import {
    ButtonColorOption,
    ButtonShapeOption,
} from '@eventbrite/paypal-checkout-types';
import classNames from 'classnames';
import React, {
    FunctionComponent,
    ReactNode,
    useEffect,
    useState,
} from 'react';
import {
    CTA_CHECKOUT_TEXT,
    CTA_COMPLETE_FREE_TEXT,
    CTA_PAY_ANOTHER_WAY,
} from '../../constants';
import { ScalableExpressCheckoutExperimentVariant } from '../../constants/statsig/types';
import { useOnExpressPaymentMethodsFullyLoaded } from '../../containers/ticketSelection/useOnExpressPaymentMethodsFullyLoaded';
import type {
    ExpressCheckoutSubmitDataType,
    ExpressPaymentMethodStatus,
} from '../../models/expressCheckout';
import { StatsigValueType } from '../../models/expressCheckout';
import { useIsGooglePayOnTicketSelectionExperimentEnabled } from '../../utils/hooks/statsig';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';
import { getScalableExpressCheckoutExperimentValue } from '../../utils/statsig';
import { ConnectedApplepayNativeButton } from '../paymentMethodForms/ApplepayNativeButton/ConnectedApplepayNativeButton';
import { GooglePayButton } from '../paymentMethodForms/GooglePayButton';
import { ConnectedPaypalButton } from '../paymentMethodForms/PaypalPaymentFields/ConnectedPaypalButton';
import commonStyles from './ExpressCheckout.common.module.scss';
import defaultStyles from './ExpressCheckout.module.scss';
import rebrandStyles from './ExpressCheckout.rebranding.module.scss';
import { getExpressPaymentMethodsAvailability } from './getExpressPaymentMethodsAvailability';
import LegalText from './LegalText';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};
interface ExpressPaymentMethod {
    node: ReactNode;
    isLoading: boolean;
}

export interface ExpressCheckoutProps {
    areAllSelectedTicketsFree: boolean;
    areTicketsSelected: boolean;
    handleExpressPaymentSubmit: (props: ExpressCheckoutSubmitDataType) => void;
    handleSubmit: (props: ExpressCheckoutSubmitDataType) => void;
    hasApplePayInstance: boolean;
    hasAsyncApplePayErrors: boolean;
    isSeriesInfiniteCalendar: boolean;
    initializeGooglePayPaymentRequest: () => void;
    initializePaypal: () => void;
    isAppRebrandActive: boolean;
    isCompactSize: boolean;
    isExpressApplePayEnabled: boolean;
    isExpressGooglePayEnabled: boolean;
    isExpressPaymentButtonDisabled: boolean;
    isExpressPayPalEnabled: boolean;
    isGooglePayScriptInitialized: boolean;
    isOnlyCheckoutPage: boolean;
    isPaypalScriptInitialized: boolean;
    isSubmitting: boolean;
    locale: string;
    applePayExpressPaymentMethodStatus: ExpressPaymentMethodStatus;
    googlePayExpressPaymentMethodStatus: ExpressPaymentMethodStatus;
    paypalExpressPaymentMethodStatus: ExpressPaymentMethodStatus;
    onFullyLoaded?: () => void;
    areEventbriteLegacyPaymentMethodsDisabled: boolean;
}

const SkeletonButton: FunctionComponent<{ styles: Record<string, string> }> = ({
    styles,
}) => (
    <div className={styles.skeletonButton} data-testid="skeleton-button">
        <span className="eds-is-hidden-accessible">
            {gettext('Loading...')}
        </span>
    </div>
);

const getCtaText = ({
    hasApplePayInstance,
    areOnlyFreeTicketsSelected,
    isExpressPayPalEnabled,
    isExperimentAndExpressGooglePayEnabled,
    areEventbriteLegacyPaymentMethodsDisabled,
}: {
    hasApplePayInstance: boolean;
    areOnlyFreeTicketsSelected: boolean;
    isExpressPayPalEnabled: boolean;
    isExperimentAndExpressGooglePayEnabled: boolean;
    areEventbriteLegacyPaymentMethodsDisabled: boolean;
}) => {
    if (areOnlyFreeTicketsSelected) {
        return CTA_COMPLETE_FREE_TEXT;
    }

    if (areEventbriteLegacyPaymentMethodsDisabled) {
        return CTA_CHECKOUT_TEXT;
    }

    const shouldShowCtaExpressCheckoutText =
        hasApplePayInstance ||
        isExpressPayPalEnabled ||
        isExperimentAndExpressGooglePayEnabled;

    return shouldShowCtaExpressCheckoutText
        ? CTA_PAY_ANOTHER_WAY
        : CTA_CHECKOUT_TEXT;
};

export const ExpressCheckout: FunctionComponent<ExpressCheckoutProps> = ({
    areAllSelectedTicketsFree,
    areTicketsSelected,
    handleExpressPaymentSubmit,
    handleSubmit,
    hasApplePayInstance,
    hasAsyncApplePayErrors,
    initializeGooglePayPaymentRequest,
    initializePaypal,
    isAppRebrandActive,
    isCompactSize,
    isExpressApplePayEnabled,
    isExpressGooglePayEnabled: isExpressGooglePayEnabledByCheckoutConditions,
    isExpressPaymentButtonDisabled,
    isExpressPayPalEnabled,
    isGooglePayScriptInitialized,
    isOnlyCheckoutPage,
    isPaypalScriptInitialized,
    isSubmitting,
    locale,
    applePayExpressPaymentMethodStatus,
    googlePayExpressPaymentMethodStatus,
    paypalExpressPaymentMethodStatus,
    onFullyLoaded,
    isSeriesInfiniteCalendar,
    areEventbriteLegacyPaymentMethodsDisabled,
}) => {
    const isScalableExpressCheckoutEnabled =
        getScalableExpressCheckoutExperimentValue() ===
        ScalableExpressCheckoutExperimentVariant.enabled;

    const isGooglePayOnTicketSelectionExperimentEnabled =
        useIsGooglePayOnTicketSelectionExperimentEnabled();
    const isExpressGooglePayEnabled =
        isGooglePayOnTicketSelectionExperimentEnabled &&
        isExpressGooglePayEnabledByCheckoutConditions;

    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    useEffect(() => {
        if (isExpressPayPalEnabled && !isPaypalScriptInitialized) {
            initializePaypal();
        }
    }, [isExpressPayPalEnabled, isPaypalScriptInitialized, initializePaypal]);

    useEffect(() => {
        if (isExpressGooglePayEnabled && isGooglePayScriptInitialized) {
            initializeGooglePayPaymentRequest();
        }
    }, [
        isExpressGooglePayEnabled,
        isGooglePayScriptInitialized,
        initializeGooglePayPaymentRequest,
    ]);

    const [isDefaultCTALastClicked, setIsDefaultCTALastClicked] =
        useState<boolean>(false);

    const isExperimentAndExpressGooglePayEnabled =
        useIsGooglePayOnTicketSelectionExperimentEnabled() &&
        isExpressGooglePayEnabled;

    const {
        shouldShowApplePayButton,
        shouldShowGooglePayButton,
        shouldShowPaypalButton,
    } = getExpressPaymentMethodsAvailability({
        areAllSelectedTicketsFree,
        areTicketsSelected,
        hasAsyncApplePayErrors,
        isExpressApplePayEnabled,
        isExpressGooglePayEnabled,
        isExpressPayPalEnabled,
        areEventbriteLegacyPaymentMethodsDisabled,
    });

    const areOnlyFreeTicketsSelected =
        areTicketsSelected && areAllSelectedTicketsFree;

    const shouldShowLegalText =
        (hasApplePayInstance ||
            isPaypalScriptInitialized ||
            isGooglePayScriptInitialized) &&
        !areAllSelectedTicketsFree &&
        !areEventbriteLegacyPaymentMethodsDisabled;

    useOnExpressPaymentMethodsFullyLoaded({
        allowApplePay: isExpressApplePayEnabled,
        allowGooglePay: isExpressGooglePayEnabled,
        allowPayPal: isExpressPayPalEnabled,
        applePayExpressPaymentMethodStatus,
        googlePayExpressPaymentMethodStatus,
        paypalExpressPaymentMethodStatus,
        areAllSelectedTicketsFree,
        areTicketsSelected,
        hasAsyncApplePayErrors,
        areEventbriteLegacyPaymentMethodsDisabled,
        onExpressPaymentMethodsFullyLoaded:
            onFullyLoaded ||
            (() => {
                /*blank on purpose*/
            }),
    });

    const defaultCTAOnClick = () => {
        setIsDefaultCTALastClicked(true);
        return handleSubmit({
            statsigValue: StatsigValueType.DEFAULT_CTA,
        });
    };

    const expressCheckoutButtonClasses = classNames([styles.buttons], {
        [styles.buttonsScalableExpressCheckout]:
            isScalableExpressCheckoutEnabled,
        [styles.twoButtonsEnabled]:
            shouldShowApplePayButton && shouldShowPaypalButton,
        [styles.displayHorizontally]: isCompactSize || isOnlyCheckoutPage,
        [commonStyles.seriesInfiniteCalendarCheckoutButtons]:
            !isScalableExpressCheckoutEnabled && isSeriesInfiniteCalendar,
        [styles.seriesInfiniteCalendarCheckoutButtons]:
            isSeriesInfiniteCalendar,
    });

    const payPalButtonStyles = isAppRebrandActive
        ? { color: ButtonColorOption.Black, shape: ButtonShapeOption.Pill }
        : { color: ButtonColorOption.Black };

    const applePayButton: ExpressPaymentMethod = {
        node: shouldShowApplePayButton ? (
            <div
                className={styles.applePayButton}
                data-testid="express-checkout-single-button-apple-pay"
            >
                {hasApplePayInstance ? (
                    <div className="eds-fx--fade-in eds-fx--duration-2">
                        <ConnectedApplepayNativeButton
                            handleSubmit={handleSubmit}
                        />
                    </div>
                ) : !isScalableExpressCheckoutEnabled ? (
                    <SkeletonButton styles={styles} />
                ) : null}
            </div>
        ) : null,
        isLoading: shouldShowApplePayButton && !hasApplePayInstance,
    };

    const paypalButton: ExpressPaymentMethod = {
        node: shouldShowPaypalButton ? (
            <div
                className="express-checkout-single-button"
                data-testid="express-checkout-single-button-paypal"
            >
                {isPaypalScriptInitialized ? (
                    <ConnectedPaypalButton
                        handleSubmit={handleExpressPaymentSubmit}
                        isDisabled={isExpressPaymentButtonDisabled}
                        {...payPalButtonStyles}
                    />
                ) : !isScalableExpressCheckoutEnabled ? (
                    <SkeletonButton styles={styles} />
                ) : null}
            </div>
        ) : null,
        isLoading: shouldShowPaypalButton && !isPaypalScriptInitialized,
    };

    const googlePayButton: ExpressPaymentMethod = {
        node: shouldShowGooglePayButton ? (
            <div
                className="express-checkout-single-button"
                data-testid="express-checkout-single-button-gpay"
            >
                {isGooglePayScriptInitialized ? (
                    <GooglePayButton
                        buttonColor="black"
                        handleSubmit={handleExpressPaymentSubmit}
                        isDisabled={isExpressPaymentButtonDisabled}
                    />
                ) : !isScalableExpressCheckoutEnabled ? (
                    <SkeletonButton styles={styles} />
                ) : null}
            </div>
        ) : null,
        isLoading: shouldShowGooglePayButton && !isGooglePayScriptInitialized,
    };

    const ctaText = getCtaText({
        hasApplePayInstance,
        areOnlyFreeTicketsSelected,
        isExpressPayPalEnabled,
        isExperimentAndExpressGooglePayEnabled,
        areEventbriteLegacyPaymentMethodsDisabled,
    });

    const appleAndPaypalButtons =
        applePayButton.node || paypalButton.node ? (
            <div
                className={styles.buttonsWrapper}
                onClick={() => setIsDefaultCTALastClicked(false)}
            >
                {applePayButton.node}
                {paypalButton.node}
            </div>
        ) : null;

    const legalTextCss = classNames({
        'eds-fx--fade-in eds-fx--duration-2':
            !isAppRebrandActive && !isSeriesInfiniteCalendar,
        'eds-l-pad-bot-4':
            !isScalableExpressCheckoutEnabled &&
            !isAppRebrandActive &&
            !isSeriesInfiniteCalendar,
        'eds-l-pad-bot-1':
            isScalableExpressCheckoutEnabled &&
            !isAppRebrandActive &&
            !isSeriesInfiniteCalendar,
        [styles.legalTextRightAlign]:
            !isAppRebrandActive && !isSeriesInfiniteCalendar,
        [commonStyles.seriesInfiniteCalendarLegalTextWrapper]:
            !isScalableExpressCheckoutEnabled && isSeriesInfiniteCalendar,
        [styles.seriesInfiniteCalendarLegalTextWrapper]:
            !isScalableExpressCheckoutEnabled && isSeriesInfiniteCalendar,
        [styles.seriesInfiniteCalendarLegalText]:
            isScalableExpressCheckoutEnabled && isSeriesInfiniteCalendar,
    });

    const legalTextNode = shouldShowLegalText ? (
        <div className={legalTextCss}>
            <LegalText
                isAppRebrandActive={isAppRebrandActive}
                locale={locale}
                showingApplePay={shouldShowApplePayButton}
                showingPaypal={shouldShowPaypalButton}
                showingGooglePay={shouldShowGooglePayButton}
                styles={styles}
                isScalableExpressCheckoutEnabled={
                    isScalableExpressCheckoutEnabled
                }
            />
        </div>
    ) : null;

    const isExpressCheckoutSingleButtonLoading =
        isSubmitting && isDefaultCTALastClicked;

    const containerCss = classNames(
        isScalableExpressCheckoutEnabled
            ? styles.scalableExpressCheckoutContainer
            : undefined,
        {
            'eds-align--right eds-fx--fade-in eds-fx--duration-2':
                !isSeriesInfiniteCalendar,
            [commonStyles.seriesInfiniteCalendarExpressCheckoutContainer]:
                !isScalableExpressCheckoutEnabled && isSeriesInfiniteCalendar,
            [styles.seriesInfiniteCalendarExpressCheckoutContainer]:
                isSeriesInfiniteCalendar,
        },
    );

    const isPaidTicketSelection =
        areTicketsSelected && !areOnlyFreeTicketsSelected;

    // Extracted nodes to simplify render logic
    const appleAndPaypalButtonsNode = isScalableExpressCheckoutEnabled
        ? isPaidTicketSelection && appleAndPaypalButtons
        : appleAndPaypalButtons;

    const googlePayButtonNode = isScalableExpressCheckoutEnabled
        ? isPaidTicketSelection && googlePayButton.node
        : googlePayButton.node;

    const defaultCtaButtonNode = (
        <Button
            onClick={defaultCTAOnClick}
            style={isAppRebrandActive ? STYLE_REBRANDING_NEUTRAL : STYLE_FILL}
            size="block"
            type="button"
            data-spec={'eds-modal__primary-button'}
            data-automation={'eds-modal__primary-button'}
            isLoading={isExpressCheckoutSingleButtonLoading}
        >
            {ctaText}
        </Button>
    );

    const scalableDefaultCtaNode =
        areTicketsSelected && areOnlyFreeTicketsSelected ? (
            <div className={styles.expressCheckoutSingleButton}>
                {defaultCtaButtonNode}
            </div>
        ) : null;

    const classicDefaultCtaNode = (
        <div
            className={classNames({
                [styles.expressCheckoutSingleButton]: !isSeriesInfiniteCalendar,
                [commonStyles.seriesInfiniteCalendarNonExpressCheckoutCTA]:
                    isSeriesInfiniteCalendar,
                [styles.seriesInfiniteCalendarNonExpressCheckoutCTA]:
                    isSeriesInfiniteCalendar,
            })}
        >
            {defaultCtaButtonNode}
        </div>
    );

    const defaultCtaNode = isScalableExpressCheckoutEnabled
        ? scalableDefaultCtaNode
        : classicDefaultCtaNode;

    return (
        <>
            <div
                data-testid="express-checkout"
                data-no-tickets-selected={!areTicketsSelected}
                className={classNames(styles.scalableExpressCheckoutContainer, {
                    'eds-align--right eds-fx--fade-in eds-fx--duration-2':
                        !isSeriesInfiniteCalendar,
                    [styles.seriesInfiniteCalendarExpressCheckoutContainer]:
                        isSeriesInfiniteCalendar,
                })}
            >
                {legalTextNode}
                <div className={containerCss}>
                    <div className={expressCheckoutButtonClasses}>
                        {appleAndPaypalButtonsNode}
                        {googlePayButtonNode}
                        {defaultCtaNode}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ExpressCheckout;
